// ---------------------------------------------------------------------------
// The homepage's single content module — every section of / renders from the
// objects in this file, so copy has exactly one home.
//
// History: born as design-lab/content.js, shared by four prototype directions
// at /design/*. The Data direction won and became the live homepage on
// 2026-08-04; the lab was deleted and this file promoted. (The two-homes setup
// bit us once — a stale enterprise-verticals line survived here after the
// homepage was repositioned. One file, no drift.)
//
// Redaction (unchanged from the lab):
//   - Real, redacted data from the short-term-rental coverage. Publishing
//     clearance confirmed 2026-07-30; participant names, business names and
//     locations are stripped — replaced with role + org size + country.
//   - Location scrubbed. The interview named a university's games and a local
//     attraction, which together pin it to one town. The eclipse is kept: it
//     crossed many states and does not localise on its own.
//   - Vendor name replaced with "[the product]" inside quotes. Airbnb and Vrbo
//     are kept — they are the customer's own channels, not the vendor under
//     review.
// ---------------------------------------------------------------------------

export const meta = {
  headline: 'See how customers use and compare products in your industry',
  // Leading-indicator wedge (2026-08-04). Interviews are with ACTIVE users —
  // the funnel's eligibility gate requires use in the last 30 days and turns
  // away anyone who has stopped. Do not reintroduce exit-interview or
  // churn-post-mortem language: it promises a session this product cannot run.
  sub:
    'You hear from the customers who write in. The quiet ones just stop paying. We pay active users in your market to walk through what they are trying to get done, how well it actually works, and which products they are weighing right now — recorded, analyzed, and refreshed throughout the year.',
  primaryCta: 'Request access',
  secondaryCta: 'See a real analysis',
  audience:
    'Built for product and product-marketing leaders in Short-term rental & property management, E-commerce & seller tools, Creator & newsletter platforms.',
  stats: [
    { value: '~10', label: 'interviews / product' },
    { value: '3×', label: 'refreshes / year' },
    { value: '8', label: 'job-map stages' },
    { value: '100%', label: 'claims with receipts' },
  ],
};

export const approach = {
  title: 'Professional customer research interviews',
  intro:
    'Every interview is run to the same structure by a moderator who knows what to probe for. Not a survey and not a scripted usability test — a guided conversation with someone who uses the product to do their job.',
  items: [
    {
      title: 'Buying behaviour',
      description: 'What triggered the original search, what they considered, and what would move them now.',
    },
    {
      title: 'What they are weighing today',
      description: 'The alternatives still on their mind — the read no vendor gets from their own users.',
    },
    {
      title: 'How well the Job got done',
      description: 'Every Job scored for importance and satisfaction, so the gaps are visible before anyone cancels.',
    },
  ],
};

// --- Deliverable 01: the Customer Story -------------------------------------
// Ledger layout; rows render in `journey` array order. The 2026-08-04 review
// set that order: Jobs first (what she is trying to get done frames everything
// after it), then the switching narrative, then Future goals as the close.
// Receipts are deliberately withheld on Evaluated and Verdict — those rows are
// meant to scan; the hook and the breaks-down rows still carry their quotes.

export const story = {
  label: 'Deliverable 01',
  kicker: 'One interview',
  title: 'Every interview is analysed to this depth.',
  intro:
    'What made them start looking, what they weighed, what they chose, and where it is failing them — every claim backed by a verbatim quote and timestamp in the underlying record.',
  participant: 'Short-term rental host / owner · independent operator · US',
  duration: '~30 min recorded session',

  hook: {
    label: 'The moment it broke',
    text:
      'During a total solar eclipse, nearby listings went to $1,000–$2,158 a night. Her automated pricing did not move.',
    quote:
      'Their prices were somewhere even double what they usually are… I looked at mine and mine was still the same. It hadn’t gone up… I don’t know if I got the wrong idea on Dynamic pricing or if it’s just not working the way it should be.',
    timestamp: '02:04–02:32',
  },

  journey: [
    {
      id: 'jobs',
      step: 'Jobs To Be Done',
      caption: 'Stated independently of any product — what she is actually trying to get done',
      items: [
        {
          sentence: 'I want to adjust my nightly rates appropriately so I can capture higher revenue',
          importance: 9,
          satisfaction: 4,
        },
        {
          sentence:
            'I want to keep prices consistent across my booking channels so I can avoid manual updates',
          importance: 10,
          satisfaction: 2,
          highlight: true,
        },
      ],
      payoff:
        'Importance 10, satisfaction 2. That gap — not how often a job comes up — is the opportunity.',
    },
    {
      id: 'previous',
      step: 'Previous solution',
      caption: 'What she was doing before she started looking',
      headline: 'Airbnb’s built-in Smart Pricing — automated rates that never reacted to local events',
      note: 'The failure of the previous solution is what the trigger below is phrased against.',
    },
    {
      id: 'trigger',
      step: 'Trigger',
      caption: 'The moment they became a buyer',
      headline:
        'When I realized my dynamic pricing wasn’t adjusting for major local events and couldn’t sync prices across my channels',
      note: 'Triggers are phrased as a deficiency, never as a need. This is what campaign timing is written against.',
    },
    {
      id: 'considered',
      step: 'Evaluated',
      caption: 'The shortlist of considered solutions, and why each lost',
      items: [
        {
          name: 'Another dynamic pricing service with cross-platform sync',
          reasons: [
            {
              reason: 'Still evaluating the incumbent; wants to try comp-set configuration first',
              weight: 6,
            },
          ],
        },
        {
          name: 'Manually updating Vrbo prices to mirror Airbnb',
          reasons: [
            { reason: 'Too time-consuming to maintain daily across months', weight: 9 },
            { reason: 'Would create inconsistent pricing across channels', weight: 7 },
          ],
        },
      ],
    },
    {
      id: 'chose',
      step: 'Chosen Solution',
      caption: 'What they actually do now',
      headline:
        'I use automated Smart Rates to price my Airbnb listing, and manually adjust special-event days',
    },
    {
      id: 'verdict',
      step: 'Verdict',
      caption: 'How it is actually going',
      satisfaction: 4,
      satisfactionOf: 10,
      drawbacks: [
        { text: 'No Vrbo integration — prices must be re-entered by hand', weight: 9 },
        { text: 'Event-driven price spikes not reflected by automated rates', weight: 8 },
        { text: 'Does not integrate with her direct-booking website', weight: 7 },
        { text: 'Navigation and discoverability gaps when setting up comps', weight: 6 },
        { text: 'Algorithm relies on posted booking data, not event calendars', weight: 6 },
      ],
      positives: [
        { text: 'Comp set and pacing data for strategy', weight: 7 },
        { text: 'Can manually override rates for specific days', weight: 6 },
      ],
      annotation:
        'Her chosen solution scores 4/10. So these drawbacks are not a satisfied customer grumbling — they are the gap a competitor walks into.',
    },
    {
      id: 'breaks',
      step: 'Where it breaks down',
      caption: 'Situations where she won’t use it at all',
      items: [
        {
          situation: 'Syncing prices to Vrbo',
          quote:
            'You’ll have to go to every day… and enter that price manually in VRBO and I’m like that’s not happening…',
          timestamp: '02:32–03:05',
        },
        {
          situation: 'Syncing prices to her direct-booking website',
          quote: 'So what about — does it talk to anything else? Because I have my own website.',
          timestamp: '04:28–04:38',
        },
        {
          situation: 'Researching which areas are worth investing in',
          quote:
            'I had heard that I could… look and see what areas are good for Airbnb investing, and I have not been able to do that task either.',
          timestamp: '05:43–06:01',
        },
      ],
      note: 'The boundaries of adoption — where a product loses to something else in a specific context. This is where positioning and expansion opportunities hide.',
    },
    {
      id: 'future',
      step: 'Future goals',
      caption: 'Where she is headed next',
      items: [{ goal: 'Buying my next rental property' }],
      note: 'Captured per interview and rolled up across the library — the demand that hasn’t hit your roadmap yet.',
    },
  ],
};

// --- Deliverable 02: the Job Map + competitive position ---------------------
// Heatmap of stage averages, four approaches. ILLUSTRATIVE SAMPLE: the review
// asked for a fully populated grid, so cells the interviews did not cover are
// filled with plausible dummy values here. The product itself never does this
// — an uncovered step renders as "no evidence" — but a marketing sample full of
// dashes read as missing data rather than honesty. Keep this comment.

export const jobMap = {
  label: 'Deliverable 02',
  kicker: 'Aggregated across the library',
  title: 'Then that Job is mapped across everyone who does it.',
  intro:
    'Interviews roll up into one map of the Job — every stage, every step, and how each approach performs on it. Your product sits in the comparison, so the read is not “here’s what a competitor does” but “here’s where you stand.”',
  job: 'I want to set and sync optimal prices',
  meta: 'Scored 1–10 from the interviews · 8 canonical stages',
  columns: ['Your product', 'Doing it manually', 'Airbnb Dynamic Pricing', 'PriceLabs'],
  stages: [
    {
      stage: 'Define',
      steps: [
        {
          step: 'Set pricing posture by season and day type',
          scores: [3, 5, 4, 7],
          reasons: [
            'Weekend-vs-midweek rules aren’t supported, so users work around it by hand.',
            '“Balanced” reads too high, so they fall back to weekend premiums.',
            'Single aggressiveness slider; no day-type control.',
            'Day-of-week and seasonal profiles supported directly.',
          ],
        },
        {
          step: 'Establish revenue and occupancy goals',
          scores: [4, 6, 3, 6],
          reasons: [
            'No explicit goal-setting; users keep targets in their heads.',
            'Goals are inferred from last season’s results rather than set explicitly.',
            'No goal concept at all.',
            'Occupancy targets exist but are buried in settings.',
          ],
        },
      ],
    },
    {
      stage: 'Locate',
      steps: [
        {
          step: 'Find comparable listings and market rates',
          scores: [7, 7, 4, 8],
          reasons: [
            'Works, but users need coaching to pick the right comparison set.',
            'Competitors and their rates are easy enough to find and benchmark.',
            'Comp data is invisible — users can’t see what drives the price.',
            'Comp sets are explicit and editable.',
          ],
        },
      ],
    },
    {
      stage: 'Prepare',
      steps: [
        {
          step: 'Check the calendar is open for target dates',
          scores: [4, 5, 5, 6],
          reasons: [
            'Users double-check openness because a previous sync blocked dates.',
            'Manual calendar review — tedious but transparent.',
            'Calendar state visible but not surfaced during pricing.',
            'Sync status shown per channel.',
          ],
        },
      ],
    },
    {
      stage: 'Confirm',
      steps: [
        {
          step: 'Verify the plan before it goes live',
          scores: [5, 6, 3, 6],
          reasons: [
            'Preview exists but users aren’t sure it reflects what will publish.',
            'What you typed is what you get.',
            'Prices publish immediately with no review step.',
            'Preview calendar before pushing.',
          ],
        },
      ],
    },
    {
      stage: 'Execute',
      steps: [
        {
          step: 'Activate price sync to channels',
          scores: [6, 3, 5, 8],
          reasons: [
            'Connects and activates immediately; one user saw a full-calendar block.',
            'Every price re-entered by hand on every channel.',
            'Airbnb-only; other channels untouched.',
            'Multi-channel sync is the headline feature.',
          ],
        },
      ],
    },
    {
      stage: 'Monitor',
      steps: [
        {
          step: 'Verify price parity across systems',
          scores: [3, 4, 3, 7],
          reasons: [
            'Base price doesn’t match the channel; users can’t tell which is live.',
            'Parity is whatever you last typed — drift is invisible until a guest books.',
            'No cross-channel view exists.',
            'Parity dashboard per channel.',
          ],
          highlight: true,
        },
      ],
    },
    {
      stage: 'Modify',
      steps: [
        {
          step: 'Override prices for special events',
          scores: [6, 7, 4, 7],
          reasons: [
            'Manual overrides work per-day but are easy to forget.',
            'Full control — the reason people tolerate the labour.',
            'Overrides exist but revert unpredictably.',
            'Event-based overrides with date ranges.',
          ],
        },
      ],
    },
    {
      stage: 'Conclude',
      steps: [
        {
          step: 'Review what the pricing strategy earned',
          scores: [4, 3, 3, 6],
          reasons: [
            'Revenue reporting exists but doesn’t tie back to pricing decisions.',
            'Spreadsheet arithmetic after the season ends.',
            'Basic earnings report, no pricing attribution.',
            'Pacing and revenue reports tied to the strategy.',
          ],
        },
      ],
    },
  ],
  legend: 'Stage averages, 1–10. Red ≤ 4, amber 5–6, green ≥ 7.',
};

// SWOT, folded into the Job Map section beneath the heatmap. Two groups per
// the 2026-08-04 review: win (Strengths + Opportunities) and exposed
// (Weaknesses + Threats), each item tagged with its original quadrant letter.

export const swot = {
  title: 'Where to focus',
  intro:
    'The map is evidence. This is the decision — where your product is winning, where it is exposed, and which gaps nobody in the category has closed yet.',
  groups: [
    {
      key: 'win',
      heading: 'Where you win',
      items: [
        {
          tag: 'S',
          text: 'Locate is your strongest stage — comparison-set building scores 7/10, level with doing it by hand, without the hand work.',
          cite: 'Locate · 7/10',
        },
        {
          tag: 'O',
          text: 'Keeping prices aligned across channels is the single worst-served Job in the library: importance 10, satisfaction 2. Customers refuse the manual workaround outright rather than tolerate it.',
          cite: 'Job gap · 10 → 2',
          lead: true,
        },
        {
          tag: 'O',
          text: 'Prepare is weak for everyone. Nobody has solved keeping a comparison set current, so it is open ground.',
          cite: 'Prepare · 4/10 best in class',
        },
      ],
    },
    {
      key: 'exposed',
      heading: 'Where you’re exposed',
      items: [
        {
          tag: 'W',
          text: 'Monitor is your weakest covered step. The base price in your product doesn’t match the channel, and users can’t tell which one is live.',
          cite: 'Monitor · 3/10',
        },
        {
          tag: 'W',
          text: 'Define is barely served. Posture can’t be set by weekday vs. weekend, so users fall back to manual overrides on the step that shapes every price after it.',
          cite: 'Define · 3/10 vs. 5/10 manual',
        },
        {
          tag: 'T',
          text: 'Customers are actively shopping for cross-platform sync and finding competitors who advertise it.',
          cite: 'Rejection reason · weight 9',
        },
        {
          tag: 'T',
          text: 'Missing a demand spike is remembered as lost revenue, not as a missing feature.',
          cite: 'Drawback · weight 8',
        },
      ],
    },
  ],
};

// --- Claude connector ------------------------------------------------------

export const connector = {
  kicker: 'Beyond the packaged views',
  title: 'Every insight is queryable. Connect Claude and just ask.',
  intro:
    'The deliverables below are the packaged reads. Underneath them the whole library is live and structured — and it plugs into Claude as a connector, so you can interrogate it in plain language instead of waiting on a report.',
  detail:
    'Ask across every interview at once, filter by persona or product area, aggregate a category, or pull the verbatim quotes behind any number on this page. Answers cite the interviews they came from, so you can always trace a claim back to the person who said it.',
  examples: [
    'Which Jobs have the biggest gap between importance and satisfaction?',
    'What are the most common complaints from customers who chose a competitor?',
    'Show me every Wish List insight about integrations, with quotes and timestamps.',
    'Compare satisfaction between finance and operations personas.',
  ],
  footnote: 'Available as an MCP connector plus a bundled analysis skill. Works in Claude desktop, web, and Claude Code.',
};

// --- Supporting sections ---------------------------------------------------

export const problem = {
  title: 'What product leaders tell us',
  quotes: [
    'The only customers we hear from are the ones who write in. We have no idea what the quiet majority think until they stop paying.',
    'We don’t know which products our users are actually weighing against us — they’d never tell us that directly.',
  ],
  alternativesTitle: 'And the alternatives don’t close the gap',
  alternatives: [
    {
      option: 'In-app surveys and NPS',
      gap: 'Single-digit response, one-word answers, and the people already halfway out the door don’t reply at all.',
    },
    {
      option: 'Talking to your own customers',
      gap: 'Necessary, and they will never tell their current vendor which competitor they’ve been trialling.',
    },
    {
      option: 'Competitive intel platforms',
      gap: 'Track announcements, pricing pages and sales collateral. Useful, and blind to the product experience itself.',
    },
    {
      option: 'Trialling competitors yourself',
      gap: 'Anyone can open a trial. It shows you an empty product — not an operator running real volume on it.',
    },
  ],
  closer:
    'Churn is a lagging indicator. Every Job in every interview is scored for importance and satisfaction, so you see the gap while the customer is still yours to keep.',
};

export const howItWorks = {
  title: 'How it works',
  intro:
    'Roughly 10 interviews per product, across the owner-operators who run their business on it — so you see role-specific reality rather than an average.',
  steps: [
    {
      title: 'You choose the products to monitor',
      description:
        'Pick an industry and the products you compete with — including your own, so the comparison means something.',
    },
    {
      title: 'We recruit and interview their real users',
      description:
        'Active operators only — every participant has used the product in the last 30 days — paid for a ~30 minute recorded session walking through one Job end to end.',
    },
    {
      title: 'We do the analysis',
      description:
        'Every session coded: how they measure success, the quantified gaps to their ideal outcome, the alternatives they weighed, and what they want next.',
    },
    {
      title: 'You get access, and it stays current',
      description:
        'Findings land as they are produced, with verbatim quotes and timestamped clips. Refreshed 3 times a year with new interviews.',
    },
  ],
};

export const whatYouGet = {
  title: 'What lands in your account',
  intro:
    'Every interview is analysed the same way, so the library compounds instead of becoming a folder of one-off reports.',
  groups: [
    {
      heading: 'From every interview',
      items: [
        {
          title: 'What matters to them',
          description: 'How this customer measures success at the Job — in their words, not a score you have to interpret.',
        },
        {
          title: 'Opportunities',
          description: 'The gaps between what they do today and their ideal outcome, each weighted by how much it costs them.',
        },
        {
          title: 'Alternative solutions',
          description: 'What they used before, what else they considered, why they chose what they chose.',
        },
      ],
    },
    {
      heading: 'Across the library',
      items: [
        {
          title: 'Comparative roll-ups',
          description: 'Why customers pick one product over another, and where the competitive openings are.',
        },
        {
          title: 'Searchable and queryable',
          description: 'Filter by Job, persona and product area — or ask Claude directly through the connector.',
        },
        {
          title: 'Shareable evidence',
          description: 'Verbatim quotes and timestamped clips. Drop the clip in the deck and let the customer make the argument.',
        },
      ],
    },
  ],
};

export const bringYourOwn = {
  title: 'Already recording customer calls?',
  body:
    'Upload them and they run through the same pipeline — same story extraction, same job maps, same ranked pain points, in the same workspace as your competitive coverage. Your uploads stay private to your organization.',
  cta: 'Talk to us about uploads',
};

export const pricing = {
  title: 'Priced per product, because that’s how you buy.',
  intro:
    'Subscribe to the products you actually compete with. Every subscription includes three refreshes a year with new interviews, so you can see what changed rather than re-reading the same study.',
  tiers: [
    {
      name: 'Single seat',
      price: '$2,500',
      period: 'per year, per product',
      sub: 'One named user',
      featured: true,
      features: [
        'Full interview library for one product',
        '3 refreshes a year with new interviews',
        'Job maps and comparative roll-ups',
        'Claude connector access',
      ],
    },
    {
      name: 'Org-wide',
      price: '$7,500',
      period: 'per year, per product',
      sub: 'Unlimited read-only seats',
      features: [
        'Everything in Single seat',
        'Unlimited seats across your company',
        'Product, marketing and leadership access',
        'Request deeper dives from practitioners',
      ],
    },
  ],
  cta: 'Request access',
};

export const faq = {
  title: 'Frequently asked questions',
  items: [
    {
      q: 'Where does this data come from?',
      a: 'Real, verified users of the product — recruited directly, screened for genuine day-to-day use, and paid for their time. They know exactly what the session is for before they start.',
    },
    {
      q: 'Do the people being recorded know it will be shared?',
      a: 'Yes. Consent is explicit and given before recording starts. They’re told the recording will be shared with product teams, and they can request edits or full removal for 48 hours after processing.',
    },
    {
      q: 'How do you handle sensitive information on screen?',
      a: 'Two ways. Before recording, we ask participants to close unrelated tabs and avoid showing customer names or account numbers, and we remind them again at the start. After recording, every session is reviewed by a person before it reaches anyone — nothing is published automatically.',
    },
    {
      q: 'How current is the coverage?',
      a: 'Each product is refreshed three times a year with new interviews, so you see how the experience changes over time rather than a single point-in-time read.',
    },
    {
      q: 'Can you include our own product in the comparison?',
      a: 'Yes — and you should. The comparison is far more useful when your product sits in it, because the output stops being “here is what a competitor does” and becomes “here is where you stand.”',
    },
    {
      q: 'Do you interview customers who have already left?',
      a: 'No — and that’s deliberate. Every participant has used the product within the last 30 days; anyone who has stopped is screened out. A post-mortem tells you why someone left after you can no longer keep them. We score every Job for importance and satisfaction while the customer is still active, so a 9-importance job sitting at 2-satisfaction shows up as a risk you can still act on.',
    },
    {
      q: 'Is this a replacement for our own user research?',
      a: 'No — it’s the half you can’t run yourself. You can talk to your own users, but they won’t tell you which competitor they’ve been trialling, and you can’t talk to your competitor’s users at all. That’s the part we cover.',
    },
  ],
};

export const footer = {
  tagline: 'See how real customers use the products you compete with.',
  email: 'hi@conversation-intelligence.com',
};
